import React, { createContext, useContext, useReducer, useMemo, useEffect, useRef, useCallback } from 'react';
import { 
  collection, 
  getDocs, 
  setDoc, 
  doc, 
  onSnapshot,
  writeBatch,
  deleteDoc,
  getCountFromServer,
  serverTimestamp,
  getDoc,
  runTransaction,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../firebaseConfig';
import { Vendor, Product, Order, SystemMessage, Driver, Client, OrderStatus, Role, Supplier, MessageSender } from '../types';
import { INITIAL_VENDORS, INITIAL_PRODUCTS, INITIAL_DRIVERS, INITIAL_CLIENTS } from '../constants';
import { safeStorage } from '../utils/safeStorage';
import { calculateSplit } from '../utils/splitCalculator';
import { installStorageGuard, purgeOrdersFromStorage } from '../utils/storageGuard';
import {
  resolveVendorDocIdForAuth,
  resolveEffectiveUserRole,
} from '../utils/resolveVendorByAuthUid';
import { assertCanDispatchOrder } from '../utils/paymentPolicy';
import {
  enrichProductL6Fields,
  mergeAndEnrichProductL6,
} from '../utils/enrichProductL6Fields';
import { toast } from '../components/Toast';
import { omitServerOnlyStoreFields, removeUndefinedFields } from '../utils/storeSavePayload';

/** Fase 2.3 — mensagem legível de erro Firebase para toast do lojista. */
function firebaseErrMsg(err: unknown): string {
  const e = err as { code?: string; message?: string };
  if (e?.code && e?.message) return `${e.code}: ${e.message}`;
  if (e?.message) return e.message;
  return 'Falha ao salvar no servidor';
}

export type UpdateOrderStatusResult = { ok: boolean; error?: string };

// ?????????????????? INSTALAR STORAGE GUARD IMEDIATAMENTE
if (typeof window !== 'undefined') {
  installStorageGuard();
  purgeOrdersFromStorage(); // Limpar dados antigos
}

// ============================================
// ?????????????????? VALIDADORES DE DADOS
// ============================================
const validateOrder = (order: any): order is Order => {
  if (!order || typeof order !== 'object') return false;
  if (!order.id || typeof order.id !== 'string') return false;
  if (!order.items || !Array.isArray(order.items)) return false;
  if (typeof order.total !== 'number' || isNaN(order.total)) return false;
  if (!order.status) return false;
  return true;
};

const validateProduct = (product: any): product is Product => {
  if (!product || typeof product !== 'object') return false;
  if (!product.id || typeof product.id !== 'string') return false;
  if (!product.name) return false;
  if (typeof product.price !== 'number' || isNaN(product.price)) return false;
  return true;
};

/** Garante name exibível — cadastros antigos só têm loja/email */
function normalizeVendorForState(input: any): Vendor {
  if (!input || typeof input !== 'object' || typeof input.id !== 'string' || !input.id) {
    return input as Vendor;
  }
  const emailLocal =
    typeof input.email === 'string' && input.email.includes('@')
      ? input.email.split('@')[0].trim()
      : '';
  const name =
    (typeof input.name === 'string' && input.name.trim()) ||
    (typeof input.storeName === 'string' && input.storeName.trim()) ||
    (typeof input.businessName === 'string' && input.businessName.trim()) ||
    (typeof input.ownerName === 'string' && input.ownerName.trim()) ||
    emailLocal ||
    'Ambulante';
  return { ...input, name } as Vendor;
}

/** Snapshot antigo (cache) não pode recolocar o aviso vermelho depois que a chave foi aceita. */
function keepConfirmedPix<T extends { payment?: { pix?: { status?: string; key?: string; keyRaw?: string } } }>(
  incoming: T,
  previous: T | undefined,
): T {
  const prevPix = previous?.payment?.pix;
  const nextPix = incoming?.payment?.pix;
  const prevKey = String(prevPix?.key || prevPix?.keyRaw || '').trim();
  const nextKey = String(nextPix?.key || nextPix?.keyRaw || '').trim();
  if (prevKey && !nextKey) {
    return {
      ...incoming,
      payment: { ...(incoming.payment || {}), pix: prevPix },
    };
  }
  return incoming;
}

const validateVendor = (vendor: any): vendor is Vendor => {
  if (!vendor || typeof vendor !== 'object') return false;
  if (!vendor.id || typeof vendor.id !== 'string') return false;
  return true;
};

const validateDriver = (driver: any): driver is Driver => {
  if (!driver || typeof driver !== 'object') return false;
  if (!driver.id || typeof driver.id !== 'string') return false;
  if (!driver.name) return false;
  return true;
};

const validateClient = (client: any): client is Client => {
  if (!client || typeof client !== 'object') return false;
  if (!client.id || typeof client.id !== 'string') return false;
  return true;
};

const validateSystemMessage = (m: any): m is SystemMessage => {
  if (!m || typeof m !== 'object') return false;
  if (!m.id || typeof m.id !== 'string') return false;
  if (!m.vendorId || typeof m.vendorId !== 'string') return false;
  if (typeof m.content !== 'string') return false;
  if (!m.sender || typeof m.sender !== 'string') return false;
  if (typeof m.read !== 'boolean') return false;
  return true;
};

/** Converte documento Firestore (Timestamp etc.) para SystemMessage */
function systemMessageFromFirestoreDoc(d: QueryDocumentSnapshot): SystemMessage | null {
  const raw = d.data() as Record<string, unknown>;
  const id = d.id;
  let ts = raw.timestamp;
  if (ts && typeof ts === 'object' && ts !== null && 'toDate' in ts) {
    ts = (ts as Timestamp).toDate().toISOString();
  } else if (typeof ts !== 'string') {
    ts = new Date().toISOString();
  }
  const msg: SystemMessage = {
    id,
    vendorId: String(raw.vendorId ?? ''),
    content: String(raw.content ?? ''),
    sender: raw.sender as MessageSender,
    targetRole: (raw.targetRole as SystemMessage['targetRole']) ?? undefined,
    timestamp: ts as string,
    read: Boolean(raw.read),
    attachment: raw.attachment as SystemMessage['attachment'],
    metadata: raw.metadata as SystemMessage['metadata'],
  };
  return validateSystemMessage(msg) ? msg : null;
}

// ============================================
// ???????????? AUTO-LIMPEZA DE CACHE (Estilo iFood/Uber)
// ============================================
const CACHE_VERSION = 'v5'; // Incrementar para for?ar limpeza
const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 horas
const CACHE_MAX_SIZE_KB = 500; // M?ximo 500KB por item

const autoCleanCache = () => {
  try {
    const lastCleanup = localStorage.getItem('eambulante_last_cleanup');
    const cacheVersion = localStorage.getItem('eambulante_cache_version');
    const now = Date.now();

    // Limpar se vers?o diferente ou cache muito antigo
    if (cacheVersion !== CACHE_VERSION || !lastCleanup || (now - parseInt(lastCleanup)) > CACHE_MAX_AGE_MS) {
      
      // Remover TODOS os dados antigos do e-ambulante
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('eambulante_') || key.startsWith('eambulante-'))) {
          // Verificar tamanho
          const data = localStorage.getItem(key);
          const sizeKB = data ? new Blob([data]).size / 1024 : 0;
          
          // Remover se muito grande ou vers?o antiga
          if (sizeKB > CACHE_MAX_SIZE_KB || key.includes('_v4') || key.includes('_v3')) {
            keysToRemove.push(key);
          }
        }
      }
      
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });
      
      // Marcar limpeza feita
      localStorage.setItem('eambulante_cache_version', CACHE_VERSION);
      localStorage.setItem('eambulante_last_cleanup', now.toString());
    }
  } catch (e) {
    console.warn('?????????????????? [Cache] Erro na auto-limpeza:', e);
  }
};

// Executar auto-limpeza no carregamento
autoCleanCache();

// ============================================
// ???????????? AUTO-CORRE??????????????????O DE PEDIDOS INCONSISTENTES
// ============================================
// Pedidos antigos (>24h) com status n?o-final
// s?o automaticamente filtrados para evitar confus?o
const autoFixInconsistentOrders = async (orders: Order[]): Promise<Order[]> => {
  const cutoffTime = Date.now() - (24 * 60 * 60 * 1000); // 24 horas
  const FINAL_STATUSES = ['completed', 'delivered', 'cancelled', 'archived'];
  const inconsistent: Order[] = [];
  const valid: Order[] = [];
  
  orders.forEach(order => {
    const orderTime = order.timestamp || 0;
    
    // ?????????????? FIX: Filtrar QUALQUER pedido n?o-final com mais de 24h (n?o s? ready_for_pickup)
    if (!FINAL_STATUSES.includes(order.status) && Number(orderTime) < cutoffTime && Number(orderTime) > 0) {
      inconsistent.push(order);
    } else {
      valid.push(order);
    }
  });
  
  // Corrigir pedidos inconsistentes - filtrar da exibi??o silenciosamente
  
  return valid;
};

// ============================================
// ?????????????? MUTEX PARA SINCRONIZA??????????????????O LOCAL
// ============================================
const SYNC_LOCK_KEY = 'eambulante_sync_lock';
const SYNC_LOCK_TIMEOUT = 2000;

// ???????????? CONTROLE DE THROTTLE PARA EVITAR LOOPS
// Vari?veis de controle movidas para useRef dentro do AppProvider (evita problemas com React StrictMode)
const LISTENER_THROTTLE_MS = 500; // ????????????? Constante imut?vel - segura como global

const acquireLock = (): boolean => {
  try {
    const currentLock = localStorage.getItem(SYNC_LOCK_KEY);
    const now = Date.now();
    if (currentLock && now - parseInt(currentLock) < SYNC_LOCK_TIMEOUT) {
      return false;
    }
    localStorage.setItem(SYNC_LOCK_KEY, now.toString());
    return true;
  } catch {
    return true;
  }
};

const releaseLock = () => {
  try { localStorage.removeItem(SYNC_LOCK_KEY); } catch {}
};

// ============================================
// ??????????????? TIPOS E ESTADO
// ============================================
interface AppState {
  vendors: Vendor[];
  suppliers: Supplier[];
  products: Product[];
  orders: Order[];
  drivers: Driver[];
  clients: Client[];
  messages: SystemMessage[];
  currentUser: { id: string, role: Role } | null;
  isLoading: boolean;
  syncStatus: 'synced' | 'syncing' | 'offline' | 'error';
  promoMode: { enabled: boolean; vendorId: string | null };
}

type AppAction =
  | { type: 'SET_DATA'; payload: Partial<AppState> }
  | { type: 'UPDATE_ORDER_STATUS'; payload: { orderId: string, status: OrderStatus, driverId?: string } }
  | { type: 'UPDATE_ORDER'; payload: { orderId: string, updates: Partial<Order> } }
  | { type: 'ADD_ORDER'; payload: Order }
  | { type: 'REMOVE_ORDER'; payload: string }
  | { type: 'SET_USER'; payload: { id: string, role: Role } | null }
  | { type: 'ADD_VENDOR'; payload: Vendor }
  | { type: 'UPDATE_VENDOR'; payload: Vendor }
  | { type: 'SET_SUPPLIERS'; payload: Supplier[] }
  | { type: 'ADD_SUPPLIER'; payload: Supplier }
  | { type: 'UPDATE_SUPPLIER'; payload: Supplier }
  | { type: 'UPDATE_PRODUCT'; payload: Product }
  | { type: 'ADD_PRODUCT'; payload: Product }
  | { type: 'REMOVE_PRODUCT'; payload: string }
  | { type: 'ADD_CLIENT'; payload: Client }
  | { type: 'UPDATE_CLIENT'; payload: Client }
  | { type: 'ADD_DRIVER'; payload: Driver }
  | { type: 'UPDATE_DRIVER'; payload: Driver }
  | { type: 'ADD_MESSAGE'; payload: SystemMessage }
  | { type: 'UPDATE_MESSAGE'; payload: SystemMessage }
  | { type: 'SET_SYNC_STATUS'; payload: AppState['syncStatus'] }
  | { type: 'SET_PROMO_MODE'; payload: { enabled: boolean; vendorId: string | null } };

const AppContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  actions: {
    updateOrderStatus: (
      orderId: string,
      status: OrderStatus,
      driverId?: string
    ) => Promise<UpdateOrderStatusResult>;
    updateOrder: (orderId: string, updates: Partial<Order>, options?: { persist?: boolean }) => void;
    removeOrder: (orderId: string) => void;
    addOrder: (order: Order) => void;
    updateProduct: (product: Product) => Promise<void>;
    addProduct: (product: Product) => void;
    removeProduct: (productId: string) => Promise<boolean>;
    /** Apaga TODOS os produtos da categoria (consulta no banco, não só os da tela). Retorna quantos. */
    removeProductsInCategory: (vendorId: string, category: string, knownIds?: string[]) => Promise<number>;
    /** Move TODOS os produtos da categoria para outra. Retorna quantos. */
    moveProductsToCategory: (vendorId: string, from: string, to: string, knownIds?: string[]) => Promise<number>;
    countProductsInCategory: (vendorId: string, category: string) => Promise<number>;
    addVendor: (vendor: Vendor, options?: { persist?: boolean }) => void;
    updateVendor: (vendor: Vendor, options?: { persist?: boolean }) => void;
    addSupplier: (supplier: Supplier) => void;
    updateSupplier: (supplier: Supplier, options?: { persist?: boolean }) => void;
    addClient: (client: Client) => void;
    updateClient: (client: Client) => void;
    addDriver: (driver: Driver) => void;
    updateDriver: (driver: Driver) => void;
    addMessage: (message: SystemMessage) => void;
    updateMessage: (message: SystemMessage) => void;
    login: (id: string, role: Role) => void;
    logout: () => void;
    setPromoMode: (enabled: boolean, vendorId: string | null) => void;
    forceSync: () => Promise<void>;
  };
} | undefined>(undefined);

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_DATA':
      return { ...state, ...action.payload };
    case 'SET_SYNC_STATUS':
      return { ...state, syncStatus: action.payload };
    case 'UPDATE_ORDER_STATUS':
      return {
        ...state,
        orders: state.orders.map(o => 
          o.id === action.payload.orderId 
            ? { 
                ...o, 
                status: action.payload.status, 
                driverId: action.payload.driverId || o.driverId
                // ???????????? REMOVIDO _localUpdatedAt - Firebase ? a ???????NICA fonte de verdade
              } 
            : o
        )
      };
    case 'UPDATE_ORDER':
      return {
        ...state,
        orders: state.orders.map(o => 
          o.id === action.payload.orderId 
            ? { 
                ...o, 
                ...action.payload.updates
                // ???????????? REMOVIDO _localUpdatedAt - Firebase ? a ???????NICA fonte de verdade
              } 
            : o
        )
      };
    case 'ADD_ORDER':
      if (!validateOrder(action.payload)) {
        console.error('???????????? Pedido inv?lido ignorado:', action.payload);
        return state;
      }
      if (state.orders.some(o => o.id === action.payload.id)) {
        return state;
      }
      return { ...state, orders: [action.payload, ...state.orders] };
    case 'REMOVE_ORDER':
      return { ...state, orders: state.orders.filter(o => o.id !== action.payload) };
    case 'SET_USER':
      return { ...state, currentUser: action.payload };
    case 'ADD_VENDOR': {
      const normalized = normalizeVendorForState(action.payload);
      if (!validateVendor(normalized)) return state;
      if (state.vendors.some(v => v.id === normalized.id)) return state;
      return { ...state, vendors: [...state.vendors, normalized] };
    }
    case 'UPDATE_VENDOR': {
      const u = keepConfirmedPix(
        normalizeVendorForState(action.payload),
        state.vendors.find(v => v.id === action.payload.id),
      );
      if (!validateVendor(u)) return state;
      if (!state.vendors.some(v => v.id === u.id)) {
        return { ...state, vendors: [...state.vendors, u] };
      }
      return { ...state, vendors: state.vendors.map(v => (v.id === u.id ? u : v)) };
    }
    case 'SET_SUPPLIERS':
      return { ...state, suppliers: action.payload };
    case 'ADD_SUPPLIER': {
      if (state.suppliers.some(s => s.id === action.payload.id)) return state;
      return { ...state, suppliers: [...state.suppliers, action.payload] };
    }
    case 'UPDATE_SUPPLIER': {
      const u = keepConfirmedPix(
        action.payload,
        state.suppliers.find(s => s.id === action.payload.id),
      );
      if (!state.suppliers.some(s => s.id === u.id)) {
        return { ...state, suppliers: [...state.suppliers, u] };
      }
      return {
        ...state,
        suppliers: state.suppliers.map(s =>
          s.id === u.id ? u : s
        )
      };
    }
    case 'UPDATE_PRODUCT':
      return { ...state, products: state.products.map(p => p.id === action.payload.id ? action.payload : p) };
    case 'ADD_PRODUCT':
      if (!validateProduct(action.payload)) {
        console.error('???????? [Reducer] Produto inv?lido rejeitado:', action.payload);
        return state;
      }
      if (state.products.some(p => p.id === action.payload.id)) {
        console.warn('?????????????????? [Reducer] Produto duplicado ignorado:', action.payload.id);
        return state;
      }
      return { ...state, products: [...state.products, action.payload] };
    case 'REMOVE_PRODUCT':
      return { ...state, products: state.products.filter(p => p.id !== action.payload) };
    case 'ADD_CLIENT':
      if (!validateClient(action.payload)) return state;
      if (state.clients.some(c => c.id === action.payload.id)) return state;
      return { ...state, clients: [...state.clients, action.payload] };
    case 'UPDATE_CLIENT':
      return { ...state, clients: state.clients.map(c => c.id === action.payload.id ? action.payload : c) };
    case 'ADD_DRIVER':
      if (!validateDriver(action.payload)) return state;
      if (state.drivers.some(d => d.id === action.payload.id)) return state;
      return { ...state, drivers: [...state.drivers, action.payload] };
    case 'UPDATE_DRIVER':
      return { ...state, drivers: state.drivers.map(d => d.id === action.payload.id ? action.payload : d) };
    case 'ADD_MESSAGE':
      return { ...state, messages: [...state.messages, action.payload] };
    case 'UPDATE_MESSAGE':
      return {
        ...state,
        messages: state.messages.map((msg) =>
          msg.id === action.payload.id ? action.payload : msg
        ),
      };
    case 'SET_PROMO_MODE':

      return { ...state, promoMode: action.payload };

    default:

      return state;
  }
}

// ============================================
// ?????????????? FUN??????????????????ES DE SINCRONIZA??????????????????O FIREBASE
// ============================================

/**
 * Gravação da loja inteira não pode substituir payment.
 * O merge do Firestore troca o mapa payment inteiro: um rascunho
 * sem chave apagava a chave Pix que o vendedor acabou de salvar.
 * A chave confirmada só entra por saveConfirmedPixKey.
 */
function omitPaymentUnlessConfirmedPix(collectionName: string, data: any): any {
  if (collectionName !== 'vendors' && collectionName !== 'suppliers') return data;
  if (!data || typeof data !== 'object') return data;
  const pix = data.payment?.pix;
  const key = String(pix?.key || pix?.keyRaw || '').trim();
  if (pix?.status === 'confirmed' && key) return data;
  if (!('payment' in data)) return data;
  const { payment: _payment, ...rest } = data;
  return rest;
}

// Salvar entidade no Firebase (filtrando campos undefined)
/** Ids dos produtos da loja nessa categoria: consulta no banco + os já conhecidos pela tela. */
async function productIdsInCategory(vendorId: string, category: string, knownIds: string[]): Promise<string[]> {
  const snap = await getDocs(
    query(collection(db, 'products'), where('vendorId', '==', vendorId), where('category', '==', category))
  );
  return [...new Set([...snap.docs.map((d) => d.id), ...knownIds])];
}

/** Grava em lotes de 400 (limite do Firestore é 500 por lote). */
async function commitInChunks(
  ids: string[],
  apply: (batch: ReturnType<typeof writeBatch>, id: string) => void
): Promise<void> {
  for (let i = 0; i < ids.length; i += 400) {
    const batch = writeBatch(db);
    ids.slice(i, i + 400).forEach((id) => apply(batch, id));
    await batch.commit();
  }
}

const saveToFirebase = async (collectionName: string, data: any, id?: string): Promise<boolean> => {
  try {
    if (!id) {
      console.error(`[Firebase] saveToFirebase sem id em ${collectionName}`);
      return false;
    }
    const docRef = doc(db, collectionName, id);
    const cleanData = removeUndefinedFields(
      omitServerOnlyStoreFields(collectionName, omitPaymentUnlessConfirmedPix(collectionName, data))
    );
    await setDoc(docRef, { 
      ...cleanData, 
      updatedAt: serverTimestamp(),
      _syncedAt: Date.now()
    }, { merge: true });
    return true;
  } catch (error) {
    console.error(`[Firebase] Falha ao salvar em ${collectionName}:`, error);
    if (collectionName === 'vendors' || collectionName === 'suppliers') {
      toast.error('Não foi possível salvar', 'A alteração da loja não foi gravada. Verifique a internet e tente de novo.');
    }
    return false;
  }
};


// Limites por cole??o ????????????? evita leitura ilimitada e custo explosivo
const COLLECTION_LIMITS: Record<string, number> = {
  vendors:  300,  // m?x 300 ambulantes por cidade
  // F2: products removido — sem teto global; useScopedProducts por vendorId
  drivers:  150,  // m?x 150 entregadores
  clients:  500,  // ?ltimos 500 clientes cadastrados
  orders:   100,  // ?ltimos 100 pedidos (listener em tempo real cuida do resto)
};

// Carregar cole??o do Firebase com limite de seguran?a
async function loadFromFirebase<T>(collectionName: string, validator: (item: any) => item is T): Promise<T[] | null> {
  try {
    const maxDocs = COLLECTION_LIMITS[collectionName] ?? 200;
    const snapshot = await getDocs(query(collection(db, collectionName), limit(maxDocs)));
    const items = (snapshot as any).docs
      .map(doc => ({ id: doc.id, ...doc.data() } as unknown))
      .filter(validator) as T[];
    return items;
  } catch (error) {
    console.error(`[Firebase] Falha ao carregar ${collectionName}:`, error);
    return null;
  }
}

/** Listagem de clients s? ? permitida a OWNER / VENDOR / SUPPLIER (firestore.rules). */
function shouldLoadClientsCollection(role: Role | null): boolean {
  return role === 'OWNER' || role === 'SUPER_ADMIN' || role === 'VENDOR' || role === 'SUPPLIER';
}

/** Listagem de drivers: fiscal (CITY_HALL) pode; CLIENT n?o. */
function shouldLoadDriversCollection(role: Role | null): boolean {
  return role === 'OWNER' || role === 'SUPER_ADMIN' || role === 'VENDOR' || role === 'SUPPLIER' || role === 'CITY_HALL';
}

/** Pedidos em lote: somente perfis com permissao de listagem ampla (evita permission-denied em VENDOR). */
function shouldLoadOrdersCollection(role: Role | null): boolean {
  return role === 'OWNER' || role === 'SUPER_ADMIN' || role === 'CITY_HALL';
}

// Sincroniza??o em batch (filtrando campos undefined)
const syncBatchToFirebase = async (collectionName: string, items: any[]): Promise<boolean> => {
  try {
    const batch = writeBatch(db);
    items.forEach(item => {
      const docRef = doc(db, collectionName, item.id);
      const cleanItem = removeUndefinedFields(
        omitServerOnlyStoreFields(collectionName, omitPaymentUnlessConfirmedPix(collectionName, item))
      );
      batch.set(docRef, { 
        ...cleanItem, 
        updatedAt: serverTimestamp(),
        _syncedAt: Date.now()
      }, { merge: true });
    });
    await batch.commit();
    return true;
  } catch (error) {
    console.error(`[Firebase] Batch sync falhou para ${collectionName}:`, error);
    return false;
  }
};

// ============================================
// ???????????? PROVIDER PRINCIPAL
// ============================================
export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, {
    vendors: [], // ???????????? VAZIO - Firebase ? a fonte da verdade
    suppliers: [],   // ???????????? Firebase ? a fonte da verdade
    products: [], // ???????????? VAZIO - Firebase ? a fonte da verdade
    orders: [],
    drivers: [], // ???????????? VAZIO - Firebase ? a fonte da verdade
    clients: [],
    messages: [],
    currentUser: null,
    isLoading: true,
    syncStatus: 'syncing',
    promoMode: { enabled: false, vendorId: null }
  });
  
  const isSaving = useRef(false);
  const unsubscribers = useRef<(() => void)[]>([]);
  const stateRef = useRef(state);
  const initialLoadStartedRef = useRef(false);
  
  // ?????????????????? Refs para controle de listeners (evita problemas com React StrictMode)
  const lastListenerUpdateRef = useRef(0);
  const isListenerProcessingRef = useRef(false);
  const ordersListenerActiveRef = useRef(false);
  /** Evita registrar onAuthStateChanged + snapshots mais de uma vez (ex.: re-run do effect). */
  const realtimeListenersAttachedRef = useRef(false);
  
  // Manter ref atualizado
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // ============================================
  // ?????????????? CARGA INICIAL - FIREBASE ????????? A FONTE DA VERDADE
  // ============================================
  useEffect(() => {
    if (initialLoadStartedRef.current) return;
    initialLoadStartedRef.current = true;

    const loadAllData = async () => {
      dispatch({ type: 'SET_SYNC_STATUS', payload: 'syncing' });

      // ?????????????? FIREBASE ????????? A FONTE DA VERDADE - N?????????O usar localStorage para dados principais
      let vendors: Vendor[] = []; // ???????????? VAZIO - s? Firebase
      let products: Product[] = []; // ???????????? VAZIO - s? Firebase
      let drivers: Driver[] = []; // ???????????? VAZIO - s? Firebase
      let clients: Client[] = [];
      let orders: Order[] = []; // ???????????? APENAS do Firebase, NUNCA do localStorage
      let messages: SystemMessage[] = [];

      // ???????????? ORDERS: Firebase ? a ???????NICA fonte - N?????????O carregar do localStorage
      // Apenas mensagens do cache (tempor?rias, n?o cr?ticas)
      const cachedMessages = safeStorage.get('eambulante_messages_v5', []);
      messages = cachedMessages.slice(0, 20);

      // ?????????????? CARREGAR DO FIREBASE (fonte ?nica de verdade)
      try {
        await auth.authStateReady();
        let userRole: Role | null = null;
        let currentAuthUid: string | null = null;
        try {
          const u = auth.currentUser;
          if (u) {
            currentAuthUid = u.uid;
            const userDoc = await getDoc(doc(db, 'users', u.uid));
            if (userDoc.exists()) {
              userRole = (userDoc.data().role as Role) || null;
            }
          }
        } catch {
          /* role opcional */
        }
        const loadClients = shouldLoadClientsCollection(userRole);
        const loadDrivers = shouldLoadDriversCollection(userRole);
        const loadOrders = shouldLoadOrdersCollection(userRole);

        const [fbVendors, fbDrivers, fbClients, fbOrders] = await Promise.all([
          loadFromFirebase<Vendor>('vendors', validateVendor),
          // F2: products NÃO carregam no boot (sem limit global) — useScopedProducts por escopo
          loadDrivers ? loadFromFirebase<Driver>('drivers', validateDriver) : Promise.resolve(null),
          loadClients ? loadFromFirebase<Client>('clients', validateClient) : Promise.resolve(null),
          loadOrders ? loadFromFirebase<Order>('orders', validateOrder) : Promise.resolve(null),
        ]);

        // FIX: role DRIVER não carrega a coleção `drivers` (shouldLoadDriversCollection=false),
        // então o boot ficava com state.drivers vazio → "Driver não encontrado" (race) no painel.
        // Carrega o próprio doc do driver no boot, eliminando a janela.
        if (userRole === 'DRIVER' && currentAuthUid) {
          try {
            const ownDriverSnap = await getDoc(doc(db, 'drivers', currentAuthUid));
            if (ownDriverSnap.exists()) {
              const ownDriver = { id: ownDriverSnap.id, ...ownDriverSnap.data() } as unknown;
              if (validateDriver(ownDriver)) {
                drivers = [ownDriver as Driver];
              }
            }
          } catch (e) {
            console.warn('[AppContext] Driver individual não carregado no boot:', e);
          }
        }

        // Firebase ? a ???????NICA fonte de verdade para vendors, products, drivers
        if (fbVendors && fbVendors.length > 0) {
          vendors = fbVendors;
        }

        // ???????????? Carregar suppliers do Firestore (cole??o separada)
        // Sem filtro por supplierStatus para n?o excluir docs sem esse campo
        try {
          const suppliersSnap = await getDocs(
            query(
              collection(db, 'suppliers'),
              limit(100)
            )
          );
          const suppliersData = suppliersSnap.docs
            .map(d => ({ id: d.id, uid: d.id, ...d.data() } as Supplier))
            .filter(s => s.id && (s.companyName || s.email));

          if (suppliersData.length > 0) {
            dispatch({ type: 'SET_SUPPLIERS', payload: suppliersData });
          }
        } catch (err) {
          console.warn('[AppContext] Suppliers n?o carregados:', err);
          // N?o bloquear app ????????????? suppliers ? feature secund?ria
        }

        // F2: products permanece [] até escopo ativo (dashboard/vitrine)

        if (fbDrivers && fbDrivers.length > 0) {
          // Preservar earnings e status local dos drivers
          drivers = fbDrivers.map(fbDriver => {
            const localDriver = INITIAL_DRIVERS.find(d => d.id === fbDriver.id);
            return {
              ...fbDriver,
              earnings: (fbDriver as any).earnings || localDriver?.earnings || 0,
              isOnline: (fbDriver as any).isOnline ?? localDriver?.isOnline ?? false
            };
          });
        }

        if (fbClients && fbClients.length > 0) {
          clients = fbClients;
        }

        // ???????????? ORDERS: Firebase ? a ???????NICA fonte - SEM mesclagem com cache
        if (fbOrders && fbOrders.length > 0) {
          // ????????????? Filtrar pedidos inconsistentes antes de exibir
          const validOrders = await autoFixInconsistentOrders(fbOrders);
          orders = validOrders.sort((a, b) => Number(b.timestamp || 0) - Number(a.timestamp || 0)).slice(0, 100);
        }

        // ???????????? N?????????O dispatch SET_SYNC_STATUS aqui - ser? feito junto com SET_DATA

        // ???????????? N?????????O enviar dados de teste para Firebase em produ??o
        // Os dados iniciais (INITIAL_VENDORS etc.) s?o apenas para fallback offline local
        // Dados reais s?o cadastrados via fluxo de registro (RegistrationScreen)
        if (import.meta.env.DEV) {
          // Apenas em desenvolvimento: popular Firebase se vazio
          if (!fbVendors || fbVendors.length === 0) {
            syncBatchToFirebase('vendors', INITIAL_VENDORS);
          }
          if (!fbProducts || fbProducts.length === 0) {
            syncBatchToFirebase('products', INITIAL_PRODUCTS);
          }
          if (!fbDrivers || fbDrivers.length === 0) {
            syncBatchToFirebase('drivers', INITIAL_DRIVERS);
          }
        }

      } catch (error) {
        console.error('[App] Firebase offline:', error);
        // Em produ??o: manter arrays vazios ????????????? o listener vai trazer quando reconectar
        // Em desenvolvimento: usar dados iniciais para facilitar testes
        if (import.meta.env.DEV) {
          vendors = INITIAL_VENDORS;
          products = INITIAL_PRODUCTS;
          drivers = INITIAL_DRIVERS;
        }
        // syncStatus ficar? 'syncing' ????????????? spinner continua vis?vel at? listener trazer dados
      }

      // Carga inicial concluída — libera UI; listeners públicos atualizam vendors em seguida
      dispatch({
        type: 'SET_DATA',
        payload: {
          vendors,
          products,
          drivers,
          clients,
          orders,
          messages,
          isLoading: false,
          syncStatus: 'synced',
        },
      });
    };

    loadAllData();
  }, []);

  // ============================================
  // ???????????????? LISTENERS EM TEMPO REAL (FIREBASE) - COM DEBOUNCE + THROTTLE
  // ???????????? CR???????TICO: Evitar loops que aumentam custo!
  // ============================================
  useEffect(() => {
    // Só após load inicial; ref impede listeners duplicados se o effect re-executar
    if (state.isLoading || realtimeListenersAttachedRef.current) return;
    realtimeListenersAttachedRef.current = true;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    // ?????????????? USAR onAuthStateChanged para reagir a mudan?as de autentica??o
    // Isso garante que listeners sejam criados quando usu?rio fizer login
    const authUnsub = onAuthStateChanged(auth, async (currentUser) => {
      // ???????????? Cancelar listeners anteriores antes de criar novos
      if (unsubscribers.current.length > 0) {
        unsubscribers.current.forEach(unsub => {
          try { unsub(); } catch (e) { /* listener j? foi cancelado */ }
        });
        unsubscribers.current = [];
      }
      ordersListenerActiveRef.current = false;

      // ???????????? LISTENER PRINCIPAL DE ORDERS (tempo real) - APENAS SE AUTENTICADO
      if (currentUser) {
        console.log('[Firebase] Auth detectado, criando listeners. UID:', currentUser.uid);
        ordersListenerActiveRef.current = true;

        // ?????????????????? BUSCAR ROLE DO USU???????RIO PARA DETERMINAR QUERY CORRETA
        // Isso ? CR???????TICO para que as firestore.rules funcionem
        let userRole: Role = 'CLIENT';
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          const storedRole = userDoc.exists()
            ? (userDoc.data().role as Role) || 'CLIENT'
            : 'CLIENT';
          userRole = await resolveEffectiveUserRole(currentUser.uid, storedRole);
          console.log('[Firebase] Role do usu?rio:', userRole, storedRole !== userRole ? `(perfil: ${storedRole})` : '');
        } catch (error) {
          console.warn('[Firebase] N?o foi poss?vel buscar role, usando CLIENT:', error);
        }

        // === LISTENER DE DRIVERS ===
        if (userRole === 'OWNER' || userRole === 'SUPER_ADMIN') {
          try {
            const driversUnsub = onSnapshot(
              query(collection(db, 'drivers'), limit(COLLECTION_LIMITS.drivers)),
              (snapshot) => {
                try {
                  const fbDrivers = (snapshot as any).docs
                    .map(doc => ({ id: doc.id, ...doc.data() } as unknown))
                    .filter(validateDriver) as Driver[];
                  dispatch({ type: 'SET_DATA', payload: { drivers: fbDrivers } });
                } catch (e) {
                  console.error('[Firebase] Erro no listener drivers:', e);
                }
              },
              (error: any) => {
                if (error.code === 'permission-denied') {
                  console.warn('[Firebase] Drivers: permiss?o negada (role n?o autorizada)');
                } else if (error.code !== 'unavailable') {
                  console.error('[Firebase] Drivers listener error:', error.code);
                }
              }
            );
            unsubscribers.current.push(driversUnsub);
          } catch (error) {
            console.error('[Firebase] Falha ao criar listener drivers:', error);
          }
        }

        // === LISTENER DO PR??????????PRIO DRIVER (tempo real para role DRIVER) ===
        // Mant?m state.drivers atualizado sem depender de loadAllData()
        if (userRole === 'DRIVER') {
          try {
            const driverDocUnsub = onSnapshot(
              doc(db, 'drivers', currentUser.uid),
              (snapshot) => {
                if (snapshot.exists()) {
                  const driverData = { id: snapshot.id, ...snapshot.data() } as unknown;
                  if (validateDriver(driverData)) {
                    dispatch({ type: 'SET_DATA', payload: { drivers: [driverData as Driver] } });
                  }
                }
              },
              (error: any) => {
                if (error.code !== 'permission-denied' && error.code !== 'unavailable') {
                  console.error('[Firebase] Driver doc listener error:', error.code);
                }
              }
            );
            unsubscribers.current.push(driverDocUnsub);
          } catch (error) {
            console.error('[Firebase] Falha ao criar listener driver doc:', error);
          }
        }

        // === LISTENER DE CLIENTS ===
        if (userRole === 'OWNER' || userRole === 'SUPER_ADMIN') {
          try {
            const clientsUnsub = onSnapshot(
              query(collection(db, 'clients'), limit(COLLECTION_LIMITS.clients)),
              (snapshot) => {
                try {
                  const fbClients = (snapshot as any).docs
                    .map(doc => ({ id: doc.id, ...doc.data() } as unknown))
                    .filter(validateClient) as Client[];
                  dispatch({ type: 'SET_DATA', payload: { clients: fbClients } });
                } catch (e) {
                  console.error('[Firebase] Erro no listener clients:', e);
                }
              },
              (error: any) => {
                if (error.code === 'permission-denied') {
                  console.warn('[Firebase] Clients: permiss?o negada (role n?o autorizada)');
                } else if (error.code !== 'unavailable') {
                  console.error('[Firebase] Clients listener error:', error.code);
                }
              }
            );
            unsubscribers.current.push(clientsUnsub);
          } catch (error) {
            console.error('[Firebase] Falha ao criar listener clients:', error);
          }
        }

        // === RESOLVER vendorId REAL (pode diferir do auth UID em producao) ===
        let vendorIdForQuery = currentUser.uid;
        let vendorStoreLinked = true;
        if (userRole === 'VENDOR') {
          const resolvedId = await resolveVendorDocIdForAuth(currentUser.uid);
          if (resolvedId) {
            vendorIdForQuery = resolvedId;
          } else {
            vendorStoreLinked = false;
            console.warn(
              '[Firebase] VENDOR sem loja vinculada; listener de orders nao sera iniciado. UID:',
              currentUser.uid
            );
          }
        } else if (userRole === 'SUPPLIER') {
          try {
            const docByUid = await getDoc(doc(db, 'suppliers', currentUser.uid));
            if (docByUid.exists()) {
              vendorIdForQuery = currentUser.uid;
            } else {
              const fieldsToTry = ['uid', 'userId', 'ownerUid', 'ownerId'] as const;
              for (const field of fieldsToTry) {
                const byField = await getDocs(
                  query(collection(db, 'suppliers'), where(field, '==', currentUser.uid), limit(1))
                );
                if (!byField.empty) {
                  vendorIdForQuery = byField.docs[0].id;
                  break;
                }
              }
            }
          } catch (e) {
            console.warn('[Firebase] Erro ao resolver supplierId:', e);
          }
        }

        // === LISTENER DE ORDERS ===
        let ordersQuery;
        const skipVendorOrders = userRole === 'VENDOR' && !vendorStoreLinked;
        if (skipVendorOrders) {
          ordersQuery = null;
        } else if (userRole === 'OWNER' || userRole === 'SUPER_ADMIN' || userRole === 'CITY_HALL') {
          ordersQuery = query(
            collection(db, 'orders'),
            orderBy('timestamp', 'desc'),
            limit(100)
          );
        } else if (userRole === 'VENDOR') {
          ordersQuery = query(
            collection(db, 'orders'),
            where('vendorId', '==', vendorIdForQuery),
            orderBy('timestamp', 'desc'),
            limit(50)
          );
        } else if (userRole === 'SUPPLIER') {
          ordersQuery = query(
            collection(db, 'orders'),
            where('vendorId', '==', vendorIdForQuery),
            orderBy('timestamp', 'desc'),
            limit(50)
          );
        } else if (userRole === 'DRIVER') {
          ordersQuery = query(
            collection(db, 'orders'),
            where('driverId', '==', currentUser.uid),
            orderBy('timestamp', 'desc'),
            limit(50)
          );
        } else {
          ordersQuery = query(
            collection(db, 'orders'),
            where('clientUid', '==', currentUser.uid),
            orderBy('timestamp', 'desc'),
            limit(20)
          );
        }
        if (!ordersQuery) {
          /* VENDOR sem doc vendors/ — aguarda hydrate no App.tsx */
        } else try {
          const ordersUnsub = onSnapshot(
            ordersQuery,
            (snapshot) => {
              if (debounceTimer) clearTimeout(debounceTimer);
              debounceTimer = setTimeout(() => {
                const now = Date.now();
                if (isListenerProcessingRef.current || now - lastListenerUpdateRef.current < LISTENER_THROTTLE_MS) {
                  return;
                }
                isListenerProcessingRef.current = true;
                lastListenerUpdateRef.current = now;
                try {
                  const validOrders = (snapshot as any).docs
                    .map(doc => ({ id: doc.id, ...doc.data() } as unknown))
                    .filter(validateOrder) as Order[];
                  const sorted = validOrders
                    .sort((a, b) => Number(b.timestamp || 0) - Number(a.timestamp || 0));
                  const currentOrders = stateRef.current.orders;
                  const hasChanges =
                    sorted.length !== currentOrders.length ||
                    sorted.some((fb) => {
                      const current = currentOrders.find(o => o.id === fb.id);
                      if (!current) return true;
                      return current.status !== fb.status || current.driverId !== fb.driverId;
                    });
                  if (hasChanges) {
                    dispatch({
                      type: 'SET_DATA',
                      payload: {
                        orders: sorted.slice(0, 100).sort((a, b) =>
                          Number(b.timestamp || 0) - Number(a.timestamp || 0)
                        )
                      }
                    });
                  }
                } finally {
                  isListenerProcessingRef.current = false;
                }
              }, 300);
            },
            (error: any) => {
              if (error.code === 'permission-denied') {
                console.warn('[Firebase] Orders: permiss?o negada (usu?rio n?o autorizado)');
              } else {
                console.error('[Firebase] Listener orders falhou:', error);
              }
              isListenerProcessingRef.current = false;
              dispatch({ type: 'SET_SYNC_STATUS', payload: 'synced' });
            }
          );
          unsubscribers.current.push(ordersUnsub);
        } catch (error) {
          console.error('[Firebase] Falha ao criar listener orders:', error);
        }

      } else {
        console.log('[Firebase] Listeners autenticados desabilitados');
      }
    }); // fim do onAuthStateChanged callback

    // === LISTENERS PUBLICOS (vendors + products) ===
    // CRITICO: Ficam FORA do onAuthStateChanged
    // vendors e products tem "allow read: if true" no Firestore
    // QUALQUER visitante (logado ou nao) precisa ver as lojas
    const publicUnsubs: (() => void)[] = [];

    // === LISTENER DE VENDORS (PUBLICO) ===
    try {
      const vendorsUnsub = onSnapshot(
        query(collection(db, 'vendors'), limit(COLLECTION_LIMITS.vendors)),
        (snapshot) => {
          try {
            const prevVendors = stateRef.current.vendors;
            const prevById = new Map(prevVendors.map((v) => [v.id, v]));
            const fbVendors = (snapshot as any).docs
              .map((docSnap: QueryDocumentSnapshot) =>
                keepConfirmedPix(
                  normalizeVendorForState({ id: docSnap.id, ...docSnap.data() }),
                  prevById.get(docSnap.id),
                ),
              )
              .filter(validateVendor) as Vendor[];
            // Preserva vendors hidratados por resolveVendorByAuthUid que o limit() pode omitir
            const fbIds = new Set(fbVendors.map((v) => v.id));
            const preserved = prevVendors.filter((v) => v?.id && !fbIds.has(v.id));
            const merged = preserved.length > 0 ? [...fbVendors, ...preserved] : fbVendors;
            if (merged.length > 0) {
              dispatch({ type: 'SET_DATA', payload: { vendors: merged, syncStatus: 'synced' } });
            } else if (prevVendors.length === 0) {
              dispatch({ type: 'SET_DATA', payload: { vendors: merged } });
            }
            /* Se snapshot vazio mas ja ha vendor hidratado (ex.: Rita), nao apagar state */
          } catch (e) {
            console.error('[Firebase] Erro no listener vendors:', e);
          }
        },
        (error: any) => {
          if (error.code === 'permission-denied') {
            console.warn('[Firebase] Vendors: permissao negada');
          } else if (error.code !== 'unavailable') {
            console.error('[Firebase] Vendors listener error:', error.code);
          }
          dispatch({ type: 'SET_SYNC_STATUS', payload: 'synced' });
        }
      );
      publicUnsubs.push(vendorsUnsub);
    } catch (error) {
      console.error('[Firebase] Falha ao criar listener vendors:', error);
    }

    // === LISTENER DE SUPPLIERS (PUBLICO) ===
    try {
      const suppliersUnsub = onSnapshot(
        query(collection(db, 'suppliers'), limit(100)),
        (snapshot) => {
          try {
            const prevSuppliers = stateRef.current.suppliers;
            const prevSupplierById = new Map(prevSuppliers.map((s) => [s.id, s]));
            const fbSuppliers = (snapshot as any).docs
              .map((docSnap: QueryDocumentSnapshot) =>
                keepConfirmedPix(
                  {
                    id: docSnap.id,
                    uid: docSnap.id,
                    ...docSnap.data(),
                  } as Supplier,
                  prevSupplierById.get(docSnap.id),
                ),
              )
              .filter((s: Supplier) => s.id && (s.companyName || s.email));
            if (fbSuppliers.length > 0) {
              dispatch({ type: 'SET_SUPPLIERS', payload: fbSuppliers });
            }
          } catch (e) {
            console.error('[Firebase] Erro no listener suppliers:', e);
          }
        },
        (error: any) => {
          if (error.code === 'permission-denied') {
            console.warn('[Firebase] Suppliers: permissao negada');
          } else if (error.code !== 'unavailable') {
            console.error('[Firebase] Suppliers listener error:', error.code);
          }
        }
      );
      publicUnsubs.push(suppliersUnsub);
    } catch (error) {
      console.error('[Firebase] Falha ao criar listener suppliers:', error);
    }

    // F2: listener global de products REMOVIDO (limit 600).
    // Catálogo sob demanda: hooks/useScopedProducts.ts (where vendorId + paginação).

    // Cleanup: limpar auth listener, debounceTimer, listeners autenticados E publicos
    return () => {
      realtimeListenersAttachedRef.current = false;
      authUnsub();
      if (debounceTimer) clearTimeout(debounceTimer);
      // Listeners autenticados (orders, drivers, clients)
      unsubscribers.current.forEach(unsub => {
        try { unsub(); } catch (e) { /* listener ja foi cancelado */ }
      });
      unsubscribers.current = [];
      // Listeners publicos (vendors, suppliers — sem products)
      publicUnsubs.forEach(unsub => {
        try { unsub(); } catch (e) { /* listener ja foi cancelado */ }
      });
      ordersListenerActiveRef.current = false;
      isListenerProcessingRef.current = false;
      lastListenerUpdateRef.current = 0;
    };
  }, [state.isLoading]);

  // ============================================
  // ???????????????? CACHE LOCAL M???????NIMO (Estilo iFood/Uber)
  // ????????? ORDERS: N?????????O salvar em localStorage - Firebase ? a ???????NICA fonte
  // ============================================
  const lastSaveRef = useRef(0);
  
  useEffect(() => {
    if (state.isLoading || isSaving.current) return;
    
    // Debounce de 5s entre saves
    const now = Date.now();
    if (now - lastSaveRef.current < 5000) return;
    
    const timeoutId = setTimeout(() => {
      if (!acquireLock()) return;
      
      isSaving.current = true;
      lastSaveRef.current = Date.now();
      
      try {
        // ???????????? ORDERS: N?????????O SALVAR - Firebase ? a ???????NICA fonte de verdade
        // Isso ? intencional e obrigat?rio para evitar duplica??o/inconsist?ncia
        
        // Mensagens recentes (m?x 10) em formato compacto para evitar cache enorme.
        const compactMessages = state.messages.slice(-10).map((m) => ({
          ...m,
          attachment: m.attachment
            ? {
                ...m.attachment,
                // Nao cachear base64 de anexos para manter localStorage abaixo do limite.
                url: '',
              }
            : undefined,
        }));
        safeStorage.set('eambulante_messages_v5', compactMessages);
        
        // N?????????O SALVAR: vendors, products, drivers, clients, orders
        // Firebase ? a fonte da verdade para todos esses dados!
        
        localStorage.setItem('eambulante_timestamp', Date.now().toString());
      } catch (error) {
        // Ignorar erros de cache - Firebase ? a fonte da verdade
      } finally {
        releaseLock();
        isSaving.current = false;
      }
    }, 5000);
    
    return () => clearTimeout(timeoutId);
  }, [state.messages, state.isLoading]); // ???????????? Removido state.orders

  // ============================================
  // ???????????????? SINCRONIZA??????????????????O ENTRE ABAS - REMOVIDO
  // ???????????? Firebase onSnapshot j? faz sync em tempo real entre todas as abas
  // localStorage para orders ? PROIBIDO
  // ============================================

  // ============================================
  // ???????????? ACTIONS COM SINCRONIZA??????????????????O FIREBASE
  // ============================================
  const forceSync = useCallback(async () => {
    dispatch({ type: 'SET_SYNC_STATUS', payload: 'syncing' });
    try {
      await Promise.all([
        syncBatchToFirebase('vendors', stateRef.current.vendors),
        syncBatchToFirebase('products', stateRef.current.products),
        syncBatchToFirebase('drivers', stateRef.current.drivers),
        syncBatchToFirebase('clients', stateRef.current.clients),
      ]);
      dispatch({ type: 'SET_SYNC_STATUS', payload: 'synced' });
    } catch {
      dispatch({ type: 'SET_SYNC_STATUS', payload: 'error' });
    }
  }, []);

  const actions = useMemo(() => ({
    updateOrderStatus: async (
      orderId: string,
      status: OrderStatus,
      driverId?: string
    ): Promise<UpdateOrderStatusResult> => {
      // Para ready_for_pickup, enviar pedido COMPLETO ao Firebase
      // (motoboy precisa ver o pedido mesmo se nunca foi sincronizado antes)
      const localOrder = stateRef.current.orders.find((o) => o.id === orderId);

      if (!localOrder) {
        console.error('[AppContext] Pedido não encontrado no estado local:', orderId);
        return { ok: false, error: 'Pedido não encontrado no estado local' };
      }

      // OS 07-b5 H2: trava ready E ready_for_pickup se pré-pago (PIX) sem paid|confirmed
      if (status === 'ready' || status === 'ready_for_pickup') {
        const gate = assertCanDispatchOrder(localOrder);
        if (!gate.ok) {
          console.warn('[AppContext] Despacho bloqueado (pagamento):', orderId, gate.message);
          toast.error('Despacho bloqueado', gate.message);
          return { ok: false, error: gate.message };
        }
      }

      // Fase 2.3 — telemetria FAIL-OPEN: nunca bloqueia o despacho
      if (status === 'ready_for_pickup') {
        try {
          await setDoc(
            doc(db, 'orders', orderId),
            { dispatchRequestedAt: serverTimestamp() },
            { merge: true }
          );
        } catch (attemptErr: unknown) {
          console.warn(
            '[Firebase] telemetria dispatchRequestedAt falhou (seguindo despacho):',
            attemptErr
          );
        }
      }

      // Atualização otimista: UI imediata
      dispatch({ type: 'UPDATE_ORDER_STATUS', payload: { orderId, status, driverId } });

      // Transação atômica no Firebase
      try {
        const orderRef = doc(db, 'orders', orderId);
        await runTransaction(db, async (transaction) => {
          const orderDoc = await transaction.get(orderRef);

          if (!orderDoc.exists()) {
            const fullOrderData = {
              ...localOrder,
              id: orderId,
              status,
              driverId: driverId || null,
              timestamp: localOrder.timestamp || Date.now(),
              updatedAt: serverTimestamp(),
              _syncedFromLocal: true,
            };

            const cleanOrderData = Object.fromEntries(
              Object.entries(fullOrderData).filter(([_, v]) => v !== undefined)
            );

            transaction.set(orderRef, cleanOrderData);
            return;
          }

          const currentData = orderDoc.data();

          if (status === 'ready_for_pickup') {
            const { driverId: _oldDriverId, ...currentDataWithoutDriver } = currentData;

            const enrichedData = {
              ...currentDataWithoutDriver,
              status,
              driverId: driverId || null,
              timestamp: currentData.timestamp || localOrder.timestamp || Date.now(),
              vendorId: currentData.vendorId || localOrder.vendorId,
              vendorPhone: currentData.vendorPhone || localOrder.vendorPhone,
              customerName: currentData.customerName || localOrder.customerName,
              customerPhone: currentData.customerPhone || localOrder.customerPhone,
              deliveryNote: currentData.deliveryNote || localOrder.deliveryNote,
              total: currentData.total || localOrder.total,
              deliveryFee: currentData.deliveryFee || localOrder.deliveryFee,
              items: currentData.items || localOrder.items,
              readyForPickupAt: new Date().toISOString(),
              updatedAt: serverTimestamp(),
            };

            const cleanEnriched = Object.fromEntries(
              Object.entries(enrichedData).filter(([_, v]) => v !== undefined)
            );

            transaction.set(orderRef, cleanEnriched, { merge: true });
          } else {
            const acceptingUid = auth.currentUser?.uid;
            const isVendorAccept =
              currentData.status === 'pending' &&
              (status === 'preparing' || status === 'confirmed') &&
              !currentData.vendorAcceptedBy &&
              Boolean(acceptingUid);
            const acceptorName = isVendorAccept
              ? stateRef.current.vendors.find((v) => v.id === acceptingUid)?.storeName ||
                stateRef.current.suppliers.find((s) => s.id === acceptingUid)?.tradeName ||
                stateRef.current.suppliers.find((s) => s.id === acceptingUid)?.companyName ||
                auth.currentUser?.email ||
                ''
              : '';
            transaction.update(orderRef, {
              ...(isVendorAccept
                ? {
                    vendorAcceptedBy: acceptingUid,
                    vendorAcceptedByName: acceptorName,
                    vendorAcceptedAt: serverTimestamp(),
                  }
                : {}),
              status,
              ...(driverId && {
                driverId,
              }),
              ...(status === 'delivered' && !currentData.deliveredAt
                ? { deliveredAt: new Date().toISOString() }
                : {}),
              timestamp: currentData.timestamp || Date.now(),
              updatedAt: serverTimestamp(),
            });
          }
        });

        if (status === 'delivered') {
          console.log(
            '[AppContext] Crédito do vendedor será processado pela Cloud Function creditVendorOnDelivery'
          );
        }

        return { ok: true };
      } catch (error: unknown) {
        const msg1 = firebaseErrMsg(error);
        console.error('[Firebase] Erro na transação:', error);
        if (status === 'ready_for_pickup') {
          toast.error('Falha ao chamar motoboy (1ª tentativa)', msg1);
        }

        // Retry: salvamento direto como fallback
        try {
          const orderForFallback = stateRef.current.orders.find((o) => o.id === orderId);

          const effectiveDriverId =
            status === 'ready_for_pickup'
              ? null
              : driverId || orderForFallback?.driverId || null;

          await setDoc(
            doc(db, 'orders', orderId),
            {
              ...(orderForFallback || { id: orderId }),
              status,
              driverId: effectiveDriverId,
              ...(status === 'delivered' && !orderForFallback?.deliveredAt
                ? { deliveredAt: new Date().toISOString() }
                : {}),
              timestamp: orderForFallback?.timestamp || Date.now(),
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
          return { ok: true };
        } catch (retryError: unknown) {
          const msg2 = firebaseErrMsg(retryError);
          console.error('[Firebase] Fallback também falhou:', retryError);
          if (status === 'ready_for_pickup') {
            toast.error('Falha ao chamar motoboy (2ª tentativa)', msg2);
          }
          return { ok: false, error: msg2 };
        }
      }
    },
    updateOrder: (orderId: string, updates: Partial<Order>, options?: { persist?: boolean }) => {
      const order = stateRef.current.orders.find(o => o.id === orderId);
      if (order) {
        const merged = { ...order, ...updates };
        // Sync ref já (07-b5: Motoboy logo após Confirmei o PIX não pode ver pending stale)
        stateRef.current = {
          ...stateRef.current,
          orders: stateRef.current.orders.map((o) =>
            o.id === orderId ? merged : o
          ),
        };
        dispatch({ type: 'UPDATE_ORDER', payload: { orderId, updates } });
        if (options?.persist !== false) {
          saveToFirebase('orders', merged, orderId);
        }
      } else {
        dispatch({ type: 'UPDATE_ORDER', payload: { orderId, updates } });
      }
    },
    removeOrder: (orderId: string) => dispatch({ type: 'REMOVE_ORDER', payload: orderId }),
    addOrder: (order: Order) => {
      // Proteção: garantir vendorUid mesmo que o chamador esqueça
      if (order.vendorId && !order.vendorUid) {
        order.vendorUid = order.vendorId;
      }
      // Garantir que timestamp existe (CRITICO para queries do motoboy)
      // Split contabil: calcular divisao de valores
      const split = calculateSplit(order);
      const orderWithSplit = {
        ...order,
        timestamp: order.timestamp || Date.now(),
        platformFee: order.platformFee ?? split.platformFee,
        platformRate: order.platformRate ?? split.platformRate,
        vendorNet: order.vendorNet ?? split.vendorNet,
        driverEarnings: order.driverEarnings ?? split.driverEarnings,
      };
      dispatch({ type: 'ADD_ORDER', payload: orderWithSplit });
      saveToFirebase('orders', orderWithSplit, order.id);
    },
    addProduct: (product: Product) => {
      // L6.3 — todo create grava ativo/nomeLower/palavrasBusca/ordem
      const enriched = enrichProductL6Fields(product);
      dispatch({ type: 'ADD_PRODUCT', payload: enriched });
      saveToFirebase('products', enriched, enriched.id);
    },
    updateProduct: async (product: Product) => {
      if (!product?.id) {
        console.error('[updateProduct] FAIL sem id', product);
        throw new Error('Produto sem ID — não é possível salvar.');
      }
      // L6.3 — merge com produto COMPLETO antes do enrich (update parcial seguro).
      // Se o painel mandar só { id, name }, isPaused/available vêm do existente.
      let existing =
        stateRef.current.products.find((p) => p.id === product.id) || null;
      if (!existing) {
        try {
          const snap = await getDoc(doc(db, 'products', product.id));
          if (snap.exists()) {
            existing = { id: snap.id, ...snap.data() } as Product;
          }
        } catch (err) {
          console.warn('[updateProduct] fallback getDoc falhou', err);
        }
      }
      const enriched = mergeAndEnrichProductL6(existing, product);
      dispatch({ type: 'UPDATE_PRODUCT', payload: enriched });
      const ok = await saveToFirebase('products', enriched, enriched.id);
      if (!ok) {
        throw new Error('Falha ao salvar produto no Firebase');
      }
    },
    removeProduct: async (productId: string): Promise<boolean> => {
      try {
        await deleteDoc(doc(db, 'products', productId));
        dispatch({ type: 'REMOVE_PRODUCT', payload: productId });
        return true;
      } catch (error) {
        console.error('[AppContext] Falha ao excluir produto:', productId, error);
        toast.error('Não foi possível excluir', 'Verifique a internet e tente de novo.');
        return false;
      }
    },
    removeProductsInCategory: async (vendorId: string, category: string, knownIds: string[] = []) => {
      const ids = await productIdsInCategory(vendorId, category, knownIds);
      await commitInChunks(ids, (batch, id) => batch.delete(doc(db, 'products', id)));
      ids.forEach((id) => dispatch({ type: 'REMOVE_PRODUCT', payload: id }));
      return ids.length;
    },
    moveProductsToCategory: async (vendorId: string, from: string, to: string, knownIds: string[] = []) => {
      const ids = await productIdsInCategory(vendorId, from, knownIds);
      await commitInChunks(ids, (batch, id) =>
        batch.update(doc(db, 'products', id), { category: to, updatedAt: new Date().toISOString() })
      );
      return ids.length;
    },
    countProductsInCategory: async (vendorId: string, category: string) => {
      const snap = await getCountFromServer(
        query(collection(db, 'products'), where('vendorId', '==', vendorId), where('category', '==', category))
      );
      return snap.data().count;
    },
    addVendor: (vendor: Vendor, options?: { persist?: boolean }) => {
      const normalized = normalizeVendorForState(vendor);
      dispatch({ type: 'ADD_VENDOR', payload: normalized });
      if (options?.persist !== false) {
        saveToFirebase('vendors', normalized, normalized.id);
      }
    },
    updateVendor: (vendor: Vendor, options?: { persist?: boolean }) => {
      dispatch({ type: 'UPDATE_VENDOR', payload: vendor });
      if (options?.persist !== false) {
        saveToFirebase('vendors', vendor, vendor.id);
      }
    },
    addSupplier: (supplier: Supplier) => {
          dispatch({ type: 'ADD_SUPPLIER', payload: supplier });
          saveToFirebase('suppliers', supplier, supplier.id);
        },
        updateSupplier: (supplier: Supplier, options?: { persist?: boolean }) => {
          dispatch({ type: 'UPDATE_SUPPLIER', payload: supplier });
          if (options?.persist !== false) {
            saveToFirebase('suppliers', supplier, supplier.id);
          }
        },
    addClient: (client: Client) => {
      dispatch({ type: 'ADD_CLIENT', payload: client });
      saveToFirebase('clients', client, client.id);
    },
    updateClient: (client: Client) => {
      dispatch({ type: 'UPDATE_CLIENT', payload: client });
      saveToFirebase('clients', client, client.id);
    },
    addDriver: (driver: Driver) => {
      dispatch({ type: 'ADD_DRIVER', payload: driver });
      saveToFirebase('drivers', driver, driver.id);
    },
    updateDriver: (driver: Driver) => {
      dispatch({ type: 'UPDATE_DRIVER', payload: driver });
      saveToFirebase('drivers', driver, driver.id);
    },
    addMessage: (message: SystemMessage) => {
      dispatch({ type: 'ADD_MESSAGE', payload: message });
    },
    updateMessage: (message: SystemMessage) => {
      dispatch({ type: 'UPDATE_MESSAGE', payload: message });
    },
    login: (id: string, role: Role) => dispatch({ type: 'SET_USER', payload: { id, role } }),
    logout: () => dispatch({ type: 'SET_USER', payload: null }),
    setPromoMode: (enabled: boolean, vendorId: string | null) => dispatch({ type: 'SET_PROMO_MODE', payload: { enabled, vendorId } }),
    forceSync,
  }), [forceSync]);

  return (
    <AppContext.Provider value={{ state, dispatch, actions }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within an AppProvider');
  return context;
};


